import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Attachment } from '@/api/chat';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import {
  attachmentKind,
  attachmentName,
  kindLabel,
  safeAttachmentUrl,
} from '@/lib/chatAttachmentView';

/**
 * Файл из чата ученика глазами сотрудника: куратор (`CuratorChatPage`) и админ (`AdminChatMonitoringPage`),
 * техдолг 389, 12.10.2026.
 *
 * Фото — превью, по нажатию открывается целиком в новой вкладке; видео — плеером браузера, аудио — общим `AudioPlayer`;
 * PDF и прочее — карточкой со ссылкой. Под каждым — имя и вид. Размера нет: chat-service его не хранит
 * (`lib/chatAttachmentView.ts`).
 *
 * ⚠️ ПУСТОТЫ НЕ БЫВАЕТ. Файл удалён, адрес битый (на проде есть `http://x` от 17.06) или браузер не умеет формат
 * (`.mov` HEVC с iPhone в Chrome на Windows) — `onError` у фото и видео меняет их на текст и ссылку «Скачать» (у звука
 * то же делает сам `AudioPlayer`). Адреса нет или он не http(s) (`javascript:` сырым кадром сокета) — текст без ссылки
 * вовсе.
 *
 * Без водяного знака `FileVideo`: это файл ученика, а не урок.
 */
export function ChatAttachment({ attachment }: { attachment: Attachment }) {
  const { t } = useTranslation();
  const [broken, setBroken] = useState(false);
  const url = safeAttachmentUrl(attachment.url);
  const kind = attachmentKind(attachment);
  const label = kindLabel(kind, t);
  const name = attachmentName(attachment) || label;

  if (!url) {
    return (
      <div data-attach="missing" className="mb-1 flex items-start gap-1.5 text-xs text-error">
        <span className="material-symbols-outlined text-[16px] leading-none">block</span>
        <span>{t('staffChatAttach.missing')}</span>
      </div>
    );
  }

  if (broken) {
    return (
      <div data-attach="broken" className="mb-1 space-y-1 rounded-button bg-surface-container-high px-3 py-2 text-xs">
        <p className="flex items-start gap-1.5 text-error">
          <span className="material-symbols-outlined text-[16px] leading-none">error</span>
          <span>{t('staffChatAttach.broken')}</span>
        </p>
        <p className="flex items-center gap-1 text-on-surface-variant">
          <span className="min-w-0 truncate">{name}</span>
          <a href={url} target="_blank" rel="noreferrer" className="shrink-0 text-primary underline">
            {t('staffChatAttach.download')}
          </a>
        </p>
      </div>
    );
  }

  const caption = (
    <p className="mt-1 flex items-center gap-1.5 text-xs opacity-80">
      <span className="min-w-0 truncate">{name}</span>
      <span className="shrink-0">{label}</span>
      <a href={url} target="_blank" rel="noreferrer" className="shrink-0 underline">
        {t('staffChatAttach.open')}
      </a>
    </p>
  );

  if (kind === 'image') {
    return (
      <div data-attach="image" className="mb-1">
        <a href={url} target="_blank" rel="noreferrer" className="block">
          <img
            src={url}
            alt={name}
            loading="lazy"
            onError={() => setBroken(true)}
            className="max-h-48 max-w-full rounded-button object-cover"
          />
        </a>
        {caption}
      </div>
    );
  }

  if (kind === 'video') {
    return (
      <div data-attach="video" className="mb-1">
        <video
          src={url}
          controls
          preload="metadata"
          playsInline
          onError={() => setBroken(true)}
          className="max-h-64 w-full max-w-sm rounded-button bg-black"
        />
        {caption}
      </div>
    );
  }

  // Звук — только общим плеером (сторож noNativeAudioControls): ошибку и «скачать» он показывает сам.
  if (kind === 'audio') {
    return (
      <div data-attach="audio" className="mb-1 w-full max-w-xs">
        <AudioPlayer src={url} preload="none" downloadUrl={url} />
        {caption}
      </div>
    );
  }

  return (
    <a
      data-attach={kind}
      href={url}
      target="_blank"
      rel="noreferrer"
      className="mb-1 flex max-w-xs items-center gap-2 rounded-button bg-surface-container-high px-3 py-2 text-on-surface transition hover:bg-surface-container-highest"
    >
      <span className="material-symbols-outlined text-[22px] leading-none text-primary">
        {kind === 'pdf' ? 'picture_as_pdf' : 'description'}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{name}</span>
        <span className="block text-xs text-on-surface-variant">
          {label} · {t('staffChatAttach.open')}
        </span>
      </span>
    </a>
  );
}
